import React, { useEffect } from 'react'
import { array, number, func } from 'prop-types'

import { useDispatch, useSelector } from 'react-redux'
import { updateVisible } from '~/states/modules/question'

import styles from './index.module.scss'
import './styles.css'
import { inputTypes } from './utils/dictImports'
import { Divider } from 'antd'

function InputFactory({
	data: pageFieldsData,
	visible,
	disabled,
	initialValues,
	form,
	currentFormStep,
	onChange = () => {},
	onFocus = () => {},
	onCallAssistant = () => {},
	getEditor,
	setRequesterData = () => {},
}) {
	const dispatch = useDispatch()

	// Call updateVisible after first render, so the default values are considered for visibility
	useEffect(() => {
		if (typeof form !== 'undefined') {
			dispatch(
				updateVisible({
					form,
				})
			)
		}
	}, [dispatch, form, currentFormStep])

	const conditionals = useSelector(({ question }) => question.conditionals)

	const componentsInputs = []

	// set up the conditional calculations and initial values
	for (let i = 0; i < pageFieldsData.length; i++) {
		const { type, initialValue, variable } = pageFieldsData[i]
		const isVisible = visible[i] ? styles['default-style'] : styles.hidden
		const key = currentFormStep + '_' + i

		let isConditional, subConditions
		if (variable?.name !== undefined) {
			const conditions = conditionals
				.map((c) => c.split('.'))
				.filter((c) => c[0] === variable.name)

			isConditional = conditions.length > 0
			subConditions = new Set(
				conditions.map((c) => (Array.isArray(c) ? c[1] : c)).filter((c) => c)
			)
		}

		const defaultValue = () => {
			if (initialValues) {
				if (variable) {
					if (variable.hasOwnProperty('name')) {
						return initialValues[variable.name]
							? initialValues[variable.name]
							: initialValue
					} else {
						return initialValues[variable]
							? initialValues[variable]
							: initialValue
					}
				}
			}
			return initialValue
		}
		const first = i === 0

		// this functions is creating a bug for the dropdown conditional.
		// The antd select, used in the dropdown, sends 2 parameters on the onChange callback, and this function is mixing up the second
		// parameter with the subfield, which is causing the conditional to not work properly.
		let onchange = (selector = (e) => e) => {
			if (isConditional) {
				return (e, subfield) => {
					onChange()
					if (subfield === undefined || subConditions.has(subfield)) {
						dispatch(
							updateVisible({
								input: selector(e),
								form,
								fieldIndex: i,
								pageIndex: currentFormStep,
								subfield,
							})
						)
					}
				}
			}

			return onChange
		}

		// This is a temporary fix for the problem on the function above.
		let onchange_nosubfield = (selector = (e) => e) => {
			if (isConditional) {
				return (e) => {
					onChange()
					dispatch(
						updateVisible({
							input: selector(e),
							form,
							fieldIndex: i,
							pageIndex: currentFormStep,
							subfield: undefined,
						})
					)
				}
			}
			return onChange
		}

		let onfocus = (field, subfield) => {
			onFocus(subfield !== undefined ? `${field}.${subfield}` : field)
		}

		const commonProps = {
			key: key,
			pageIndex: currentFormStep,
			pageFieldsData: pageFieldsData[i],
			className: isVisible,
			disabled: disabled,
			visible: visible[i],
			inputValue: defaultValue(),
			fieldIndex: i,
			onFocus: onfocus,
			form: form,
			onChange: onchange(),
			first: first,
			setRequesterData,
		}

		if (type === 'time' || type === 'structured_checkbox') {
			commonProps.onChange = onchange((e) => e.target.value)
		}

		if (type === 'radio' || type === 'dropdown') {
			commonProps.onChange = onchange_nosubfield((e) => e)
		}

		if (type === 'text_area_ckeditor') {
			commonProps.onCallAssistant = onCallAssistant
			commonProps.getEditor = getEditor
		}

		if (type === 'separator') {
			componentsInputs.push(
				<Divider
					key={key}
					className={isVisible}
					style={{ alignSelf: 'center' }}>
					{pageFieldsData[i].title}
				</Divider>
			)
		} else if (type === 'description') {
			componentsInputs.push(
				<p key={key} className={isVisible}>
					{pageFieldsData[i].title.split('\n').map((line, index) => (
						<React.Fragment key={index}>
							{line}
							<br />
						</React.Fragment>
					))}
				</p>
			)
		} else if (inputTypes[type] !== undefined) {
			componentsInputs.push(React.createElement(inputTypes[type], commonProps))
		} else {
			componentsInputs.push(
				React.createElement(inputTypes['default'], commonProps)
			)
		}
	}
	return componentsInputs
}

InputFactory.propTypes = {
	data: array.isRequired,
	visible: array,
	currentFormStep: number,
	onChange: func,
	onFocus: func,
	getEditor: func,
	setRequesterData: func,
}

export default InputFactory
